import type {
  CannotApply,
  NotOnSheetRow,
  PricingResult,
  PricingRow,
  PricingSection,
  PricingUpdate,
} from './types';
import { str } from './wireValues';

// The pricing_update card body and the card-action reply's `pricing` (agent
// service, lender pricing import). Unlike the crew pack, this reader is strict:
// a malformed body gives null and the card shows the "can't be shown" alert,
// because a half-read card could tick a row the CA never saw.

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null;
const strOrNull = (v: unknown): string | null | undefined =>
  v === null || v === undefined ? null : typeof v === 'string' ? v : undefined;
const count = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const stringList = (v: unknown): string[] | undefined =>
  Array.isArray(v) && v.every((x) => typeof x === 'string')
    ? (v as string[])
    : undefined;

/** Reads every item or gives undefined: one bad item spoils the list. */
const all = <T>(v: unknown, read: (item: unknown) => T | undefined) => {
  if (!Array.isArray(v)) return undefined;
  const out: T[] = [];
  for (const item of v) {
    const parsed = read(item);
    if (parsed === undefined) return undefined;
    out.push(parsed);
  }
  return out;
};

function toRow(v: unknown): PricingRow | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const id = str(o.id);
  const cls = str(o.class);
  const lvr = str(o.lvr);
  const loan = str(o.loan);
  const occupancy = strOrNull(o.occupancy);
  const oldRate = strOrNull(o.oldRate);
  const newRate = strOrNull(o.newRate);
  const derivedFrom = strOrNull(o.derivedFrom);
  const flags = stringList(o.flags);
  const blocked = stringList(o.blocked);
  if (
    id === undefined ||
    cls === undefined ||
    lvr === undefined ||
    loan === undefined ||
    occupancy === undefined ||
    oldRate === undefined ||
    newRate === undefined ||
    derivedFrom === undefined ||
    flags === undefined ||
    blocked === undefined ||
    typeof o.ticked !== 'boolean'
  )
    return undefined;
  return {
    id,
    class: cls,
    occupancy,
    lvr,
    loan,
    oldRate,
    newRate,
    derivedFrom,
    flags,
    blocked,
    ticked: o.ticked,
  };
}

function toSection(v: unknown): PricingSection | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const title = str(o.title);
  const productId = str(o.productId);
  const productName = str(o.productName);
  const rows = all(o.rows, toRow);
  if (
    title === undefined ||
    productId === undefined ||
    productName === undefined ||
    rows === undefined
  )
    return undefined;
  return {
    title,
    page: count(o.page) ?? null,
    productId,
    productName,
    rows,
  };
}

function toNotOnSheet(v: unknown): NotOnSheetRow | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const id = str(o.id);
  const productName = str(o.productName);
  const lvr = str(o.lvr);
  const loan = str(o.loan);
  const occupancy = strOrNull(o.occupancy);
  const oldRate = strOrNull(o.oldRate);
  // `blocked` (ruling R20) and `flags` (R27) were added late: a missing list
  // means nothing blocks or flags it; a malformed one spoils the card.
  const flags = o.flags === undefined ? [] : stringList(o.flags);
  const blocked = o.blocked === undefined ? [] : stringList(o.blocked);
  if (
    id === undefined ||
    productName === undefined ||
    lvr === undefined ||
    loan === undefined ||
    occupancy === undefined ||
    oldRate === undefined ||
    flags === undefined ||
    blocked === undefined ||
    typeof o.ticked !== 'boolean'
  )
    return undefined;
  return {
    id,
    productName,
    occupancy,
    lvr,
    loan,
    oldRate,
    flags,
    blocked,
    ticked: o.ticked,
  };
}

function toCannotApply(v: unknown): CannotApply | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const section = str(o.section);
  const printed = str(o.printed);
  const reasons = stringList(o.reasons);
  if (section === undefined || printed === undefined || reasons === undefined)
    return undefined;
  return { section, printed, reasons };
}

/** The `body` of a pricing_update approval card, or null when it is malformed. */
export function toPricingUpdate(body: unknown): PricingUpdate | null {
  const o = obj(body);
  if (!o) return null;
  const summary = obj(o.summary);
  const pages = count(summary?.pages);
  const rates = count(summary?.rates);
  const seconds = count(summary?.seconds);
  const sections = all(o.sections, toSection);
  const notOnSheet = all(o.notOnSheet ?? [], toNotOnSheet);
  const cannotApply = all(o.cannotApply ?? [], toCannotApply);
  const notImported = stringList(o.notImported ?? []);
  const batchId = strOrNull(o.batchId);
  const lenderId = strOrNull(o.lenderId);
  const effectiveFrom = strOrNull(o.effectiveFrom);
  const sheetDate = strOrNull(o.sheetDate);
  const note = strOrNull(o.note);
  if (
    pages === undefined ||
    rates === undefined ||
    seconds === undefined ||
    sections === undefined ||
    notOnSheet === undefined ||
    cannotApply === undefined ||
    notImported === undefined ||
    batchId === undefined ||
    lenderId === undefined ||
    effectiveFrom === undefined ||
    sheetDate === undefined ||
    note === undefined
  )
    return null;
  return {
    batchId,
    lenderId,
    fileName: str(o.fileName) ?? '',
    effectiveFrom,
    sheetDate,
    summary: { pages, rates, seconds },
    sections,
    notOnSheet,
    cannotApply,
    notImported,
    note,
  };
}

/** `pricing` in a confirm/discard reply, or null when it is missing or malformed. */
export function toPricingResult(v: unknown): PricingResult | null {
  const o = obj(v);
  if (!o) return null;
  const status =
    o.status === 'applied' || o.status === 'discarded' ? o.status : null;
  const added = count(o.added);
  const ended = count(o.ended);
  const unchanged = count(o.unchanged);
  const skipped = count(o.skipped);
  const seconds = count(o.seconds);
  const lenderId = str(o.lenderId);
  const productIds = stringList(o.productIds);
  if (
    status === null ||
    added === undefined ||
    ended === undefined ||
    unchanged === undefined ||
    skipped === undefined ||
    seconds === undefined ||
    lenderId === undefined ||
    productIds === undefined
  )
    return null;
  return {
    status,
    added,
    ended,
    unchanged,
    skipped,
    seconds,
    lenderId,
    productIds,
  };
}
